import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { getCurrentBonusConfig, createBonusConfig, createDailyNote, getUsers, syncVirtualPosSubscriptions, syncShopifyOrders, getShopifyStoreUrl, setShopifyStoreUrl, initializeAll, uploadExcelPreview, uploadExcelConfirm, getCustomPlanTypes, addCustomPlanType, deleteCustomPlanType, getCostosFijos, setCostosFijos } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { 
  Settings, 
  Target,
  MessageSquare,
  Loader2,
  Plus,
  Trash2,
  Save,
  Database,
  RefreshCw,
  CheckCircle,
  Upload,
  ShoppingBag,
  Zap,
  FileSpreadsheet,
  AlertTriangle,
  X,
  DollarSign
} from 'lucide-react';
import { toast } from 'sonner';

const Configuracion = () => {
  const { isDirection } = useAuth();
  const [bonusConfig, setBonusConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showNoteDialog, setShowNoteDialog] = useState(false);
  const [noteContent, setNoteContent] = useState('');
  const [convertToTask, setConvertToTask] = useState(false);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncingShopify, setSyncingShopify] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [shopifySyncResult, setShopifySyncResult] = useState(null);
  const [shopifyStoreUrl, setShopifyStoreUrlState] = useState('');
  const [savingStoreUrl, setSavingStoreUrl] = useState(false);
  const [initializing, setInitializing] = useState(false);
  const [initResult, setInitResult] = useState(null);
  
  // Excel Upload states
  const [uploadType, setUploadType] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadPreview, setUploadPreview] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  
  // Plan types
  const [customPlans, setCustomPlans] = useState([]);
  const [newPlanName, setNewPlanName] = useState('');
  // Costos fijos
  const [cfMonto, setCfMonto] = useState('');
  const [cfDetalle, setCfDetalle] = useState('');
  const [savingCF, setSavingCF] = useState(false);
  
  const [bonusForm, setBonusForm] = useState({
    month: new Date().toISOString().slice(5, 7),
    year: new Date().getFullYear(),
    tiers: [
      { min: 1, max: 5, amount: 20000 },
      { min: 6, max: 10, amount: 40000 },
      { min: 11, max: 15, amount: 60000 },
      { min: 16, max: 999, amount: 80000 }
    ],
    freeze_affects_bonus: false
  });

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const [bonusRes, shopifyRes, plansRes, cfRes] = await Promise.all([
        getCurrentBonusConfig(),
        getShopifyStoreUrl(),
        getCustomPlanTypes(),
        getCostosFijos()
      ]);
      if (bonusRes.data) {
        setBonusConfig(bonusRes.data);
        setBonusForm({
          month: bonusRes.data.month,
          year: bonusRes.data.year,
          tiers: bonusRes.data.tiers,
          freeze_affects_bonus: bonusRes.data.freeze_affects_bonus
        });
      }
      if (shopifyRes.data?.store_url) {
        setShopifyStoreUrlState(shopifyRes.data.store_url);
      }
      setCustomPlans(plansRes.data || []);
      if (cfRes.data) {
        setCfMonto(String(cfRes.data.monto || ''));
        setCfDetalle(cfRes.data.detalle || '');
      }
    } catch (error) {
      
    } finally {
      setLoading(false);
    }
  };

  const handleAddPlan = async () => {
    if (!newPlanName.trim()) return;
    try {
      await addCustomPlanType(newPlanName.trim());
      toast.success('Plan agregado');
      setNewPlanName('');
      const res = await getCustomPlanTypes();
      setCustomPlans(res.data || []);
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const handleDeletePlan = async (name) => {
    if (!window.confirm(`Eliminar plan "${name}"?`)) return;
    try {
      await deleteCustomPlanType(name);
      toast.success('Plan eliminado');
      const res = await getCustomPlanTypes();
      setCustomPlans(res.data || []);
    } catch (e) { toast.error('Error al eliminar'); }
  };

  const handleSaveCostosFijos = async () => {
    setSavingCF(true);
    try {
      await setCostosFijos(parseInt(cfMonto) || 0, cfDetalle);
      toast.success('Costos fijos actualizados');
    } catch (e) { toast.error('Error al guardar'); }
    finally { setSavingCF(false); }
  };

  const handleSaveBonusConfig = async () => {
    setSaving(true);
    try {
      await createBonusConfig(bonusForm);
      toast.success('Configuración de bonos guardada');
      fetchConfig();
    } catch (error) {
      
      toast.error('Error al guardar configuración');
    } finally {
      setSaving(false);
    }
  };

  const handleAddTier = () => {
    setBonusForm({
      ...bonusForm,
      tiers: [...bonusForm.tiers, { min: 0, max: 0, amount: 0 }]
    });
  };

  const handleRemoveTier = (index) => {
    setBonusForm({
      ...bonusForm,
      tiers: bonusForm.tiers.filter((_, i) => i !== index)
    });
  };

  const handleTierChange = (index, field, value) => {
    const newTiers = [...bonusForm.tiers];
    newTiers[index][field] = parseInt(value) || 0;
    setBonusForm({ ...bonusForm, tiers: newTiers });
  };

  const handleExcelUpload = async (e, type) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadType(type);
    setUploading(true);
    setUploadPreview(null);
    setUploadResult(null);
    try {
      const response = await uploadExcelPreview(file, type);
      setUploadPreview(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al procesar Excel');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleConfirmUpload = async (includeAction) => {
    if (!uploadPreview) return;
    setConfirming(true);
    try {
      const records = uploadPreview.all_data.map(item => ({
        data: item.data,
        action: item.is_duplicate ? includeAction : 'insert',
        existing_id: item.existing?.id || null
      }));
      const response = await uploadExcelConfirm({
        upload_type: uploadPreview.upload_type,
        records
      });
      setUploadResult(response.data);
      setUploadPreview(null);
      toast.success(`Importacion: ${response.data.inserted} nuevos, ${response.data.updated} actualizados, ${response.data.skipped} omitidos`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al confirmar importacion');
    } finally {
      setConfirming(false);
    }
  };

  const handleSyncVirtualPos = async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      const response = await syncVirtualPosSubscriptions();
      setSyncResult(response.data);
      toast.success('Sincronizacion con Virtual POS completada');
    } catch (error) {
      
      toast.error('Error al sincronizar con Virtual POS');
    } finally {
      setSyncing(false);
    }
  };

  const handleSaveShopifyStore = async () => {
    if (!shopifyStoreUrl.trim()) {
      toast.error('URL de tienda requerida');
      return;
    }
    setSavingStoreUrl(true);
    try {
      await setShopifyStoreUrl(shopifyStoreUrl.trim());
      toast.success('URL de tienda Shopify guardada');
    } catch (error) {
      toast.error('Error al guardar URL');
    } finally {
      setSavingStoreUrl(false);
    }
  };

  const handleSyncShopify = async () => {
    setSyncingShopify(true);
    setShopifySyncResult(null);
    try {
      const response = await syncShopifyOrders();
      setShopifySyncResult(response.data);
      if (response.data.error) {
        toast.error(response.data.error);
      } else {
        toast.success('Sincronizacion con Shopify completada');
      }
    } catch (error) {
      toast.error('Error al sincronizar con Shopify');
    } finally {
      setSyncingShopify(false);
    }
  };

  const handleInitializeAll = async () => {
    if (!window.confirm('Ejecutar inicializacion completa? Esto sincronizara Virtual POS, Shopify y datos historicos.')) return;
    setInitializing(true);
    setInitResult(null);
    try {
      const response = await initializeAll();
      setInitResult(response.data);
      toast.success('Inicializacion completada');
    } catch (error) {
      toast.error('Error al inicializar: ' + (error.response?.data?.detail || error.message));
    } finally {
      setInitializing(false);
    }
  };

  const handleCreateNote = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await createDailyNote({
        content: noteContent,
        convert_to_task: convertToTask
      });
      toast.success('Nota creada');
      setShowNoteDialog(false);
      setNoteContent('');
      setConvertToTask(false);
    } catch (error) {
      
      toast.error('Error al crear nota');
    } finally {
      setSaving(false);
    }
  };

  if (!isDirection()) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <Settings className="mx-auto h-12 w-12 text-[#E8E2D7] mb-4" />
          <h2 className="text-xl font-semibold text-[#1A2835]">Acceso Restringido</h2>
          <p className="text-[#5C7083]">Esta sección es solo para Dirección</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full" data-testid="config-loading">
        <Loader2 className="h-8 w-8 animate-spin text-[#2B4257]" />
      </div>
    );
  }

  const months = [
    { value: '01', label: 'Enero' },
    { value: '02', label: 'Febrero' },
    { value: '03', label: 'Marzo' },
    { value: '04', label: 'Abril' },
    { value: '05', label: 'Mayo' },
    { value: '06', label: 'Junio' },
    { value: '07', label: 'Julio' },
    { value: '08', label: 'Agosto' },
    { value: '09', label: 'Septiembre' },
    { value: '10', label: 'Octubre' },
    { value: '11', label: 'Noviembre' },
    { value: '12', label: 'Diciembre' },
  ];

  return (
    <div className="space-y-6 animate-fade-in" data-testid="config-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="page-title">Configuración</h1>
          <p className="page-subtitle">Ajustes del sistema y configuración de bonos</p>
        </div>
        <Button 
          className="btn-primary gap-2" 
          onClick={() => setShowNoteDialog(true)}
          data-testid="btn-new-note"
        >
          <MessageSquare size={18} />
          Nueva Instrucción Diaria
        </Button>
      </div>

      {/* Bonus Configuration */}
      <div className="card" data-testid="bonus-config-section">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-[#F5F0E8] rounded-lg">
            <Target className="text-[#2B4257]" size={20} />
          </div>
          <div>
            <h3 className="font-semibold text-[#1A2835]">Configuración de Bonos</h3>
            <p className="text-sm text-[#5C7083]">Define los tramos y montos de bonificación mensual</p>
          </div>
        </div>

        <div className="space-y-6">
          {/* Month/Year Selection */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="form-label">Mes</Label>
              <select
                value={bonusForm.month}
                onChange={(e) => setBonusForm({ ...bonusForm, month: e.target.value })}
                className="form-input"
                data-testid="select-bonus-month"
              >
                {months.map((m) => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
            </div>
            <div>
              <Label className="form-label">Año</Label>
              <Input
                type="number"
                value={bonusForm.year}
                onChange={(e) => setBonusForm({ ...bonusForm, year: parseInt(e.target.value) })}
                className="form-input"
                data-testid="input-bonus-year"
              />
            </div>
          </div>

          {/* Tiers */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <Label className="form-label mb-0">Tramos de Crecimiento</Label>
              <Button 
                type="button" 
                variant="ghost" 
                size="sm" 
                className="btn-ghost gap-1"
                onClick={handleAddTier}
                data-testid="btn-add-tier"
              >
                <Plus size={14} />
                Agregar Tramo
              </Button>
            </div>
            
            <div className="space-y-3">
              {bonusForm.tiers.map((tier, index) => (
                <div 
                  key={`tier-${tier.min}-${tier.max}`} 
                  className="flex items-center gap-3 p-3 bg-[#F5F0E8] rounded-lg"
                  data-testid={`tier-${index}`}
                >
                  <div className="flex-1 grid grid-cols-3 gap-3">
                    <div>
                      <Label className="text-xs text-[#5C7083]">Desde</Label>
                      <Input
                        type="number"
                        value={tier.min}
                        onChange={(e) => handleTierChange(index, 'min', e.target.value)}
                        className="form-input"
                        min="0"
                        data-testid={`input-tier-min-${index}`}
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-[#5C7083]">Hasta</Label>
                      <Input
                        type="number"
                        value={tier.max}
                        onChange={(e) => handleTierChange(index, 'max', e.target.value)}
                        className="form-input"
                        min="0"
                        data-testid={`input-tier-max-${index}`}
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-[#5C7083]">Bono ($)</Label>
                      <Input
                        type="number"
                        value={tier.amount}
                        onChange={(e) => handleTierChange(index, 'amount', e.target.value)}
                        className="form-input"
                        min="0"
                        data-testid={`input-tier-amount-${index}`}
                      />
                    </div>
                  </div>
                  {bonusForm.tiers.length > 1 && (
                    <Button 
                      type="button" 
                      variant="ghost" 
                      size="sm" 
                      className="btn-ghost text-[#C25953] p-2"
                      onClick={() => handleRemoveTier(index)}
                      data-testid={`btn-remove-tier-${index}`}
                    >
                      <Trash2 size={16} />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Freeze Option */}
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="freeze-affects"
              checked={bonusForm.freeze_affects_bonus}
              onChange={(e) => setBonusForm({ ...bonusForm, freeze_affects_bonus: e.target.checked })}
              className="w-4 h-4 rounded border-[#E8E2D7] text-[#2B4257] focus:ring-[#2B4257]"
              data-testid="checkbox-freeze-affects"
            />
            <Label htmlFor="freeze-affects" className="text-sm cursor-pointer">
              Las congelaciones afectan el cálculo del bono
            </Label>
          </div>

          {/* Save Button */}
          <div className="flex justify-end">
            <Button 
              className="btn-primary gap-2"
              onClick={handleSaveBonusConfig}
              disabled={saving}
              data-testid="btn-save-bonus-config"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save size={16} />}
              Guardar Configuración
            </Button>
          </div>
        </div>
      </div>

      {/* Integrations & Data Migration */}
      <div className="card" data-testid="integrations-section">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-[#F5F0E8] rounded-lg">
            <Database className="text-[#2B4257]" size={20} />
          </div>
          <div>
            <h3 className="font-semibold text-[#1A2835]">Integraciones y Migración de Datos</h3>
            <p className="text-sm text-[#5C7083]">Sincroniza datos con Virtual POS y migra datos históricos desde Excel</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Initialize All - Full Deployment Setup */}
          <div className="md:col-span-2 p-5 border-2 border-[#2B4257] rounded-xl space-y-4 bg-[#F9F7F3]" data-testid="initialize-all-card">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-[#2B4257] rounded-lg">
                <Zap className="text-white" size={18} />
              </div>
              <div>
                <h4 className="font-semibold text-[#1A2835]">Inicializar Todo (Post-Deploy)</h4>
                <p className="text-xs text-[#5C7083]">Ejecuta todas las sincronizaciones de una vez: VPos, Shopify, gastos historicos</p>
              </div>
            </div>
            <Button
              className="w-full gap-2 bg-[#2B4257] hover:bg-[#1A2835] text-white"
              onClick={handleInitializeAll}
              disabled={initializing}
              data-testid="btn-initialize-all"
            >
              {initializing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Zap size={16} />}
              {initializing ? 'Inicializando...' : 'Inicializar Todos los Datos'}
            </Button>
            {initResult && (
              <div className="p-3 bg-[#EDF5F0] rounded-lg space-y-2" data-testid="init-result">
                <div className="flex items-center gap-2">
                  <CheckCircle className="text-[#4A7C59]" size={16} />
                  <span className="text-sm font-medium text-[#4A7C59]">Inicializacion completada</span>
                </div>
                {initResult.results?.current_counts && (
                  <div className="grid grid-cols-3 gap-2 text-xs text-[#5C7083]">
                    <p>Clientes: {initResult.results.current_counts.clients}</p>
                    <p>Leads: {initResult.results.current_counts.leads}</p>
                    <p>Tareas: {initResult.results.current_counts.tasks}</p>
                    <p>Casos: {initResult.results.current_counts.operational_cases}</p>
                    <p>Gastos: {initResult.results.current_counts.expenses}</p>
                    <p>Productos: {initResult.results.current_counts.products}</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Virtual POS Sync */}
          <div className="p-5 border border-[#E8E2D7] rounded-xl space-y-4" data-testid="virtualpos-sync-card">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-[#EDF5F0] rounded-lg">
                <RefreshCw className="text-[#4A7C59]" size={18} />
              </div>
              <div>
                <h4 className="font-semibold text-[#1A2835]">Sincronizar Virtual POS</h4>
                <p className="text-xs text-[#5C7083]">Importa suscripciones activas y actualiza clientes</p>
              </div>
            </div>
            <Button
              className="btn-primary w-full gap-2"
              onClick={handleSyncVirtualPos}
              disabled={syncing}
              data-testid="btn-sync-virtualpos"
            >
              {syncing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw size={16} />}
              {syncing ? 'Sincronizando...' : 'Sincronizar Ahora'}
            </Button>
            {syncResult && (
              <div className="p-3 bg-[#EDF5F0] rounded-lg space-y-1" data-testid="sync-result">
                <div className="flex items-center gap-2">
                  <CheckCircle className="text-[#4A7C59]" size={16} />
                  <span className="text-sm font-medium text-[#4A7C59]">Sincronizacion exitosa</span>
                </div>
                <p className="text-xs text-[#5C7083]">Planes: {syncResult.plans_found || 0} | Nuevos: {syncResult.new_clients || 0} | Actualizados: {syncResult.updated_clients || 0}</p>
              </div>
            )}
          </div>

          {/* Excel Upload */}
          <div className="p-5 border border-[#E8E2D7] rounded-xl space-y-4" data-testid="excel-upload-card">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-[#FAF0EC] rounded-lg">
                <FileSpreadsheet className="text-[#D98A6C]" size={18} />
              </div>
              <div>
                <h4 className="font-semibold text-[#1A2835]">Importar desde Excel</h4>
                <p className="text-xs text-[#5C7083]">Sube un .xlsx para Flujo o Control Mensual</p>
              </div>
            </div>
            <div className="flex gap-2 text-xs">
              <a href="/plantilla_gastos.xlsx" download className="text-[#2B4257] underline hover:text-[#D98A6C] transition-colors" data-testid="link-template-expenses">Descargar plantilla Gastos</a>
              <span className="text-[#E8E2D7]">|</span>
              <a href="/plantilla_control_mensual.xlsx" download className="text-[#2B4257] underline hover:text-[#D98A6C] transition-colors" data-testid="link-template-cases">Descargar plantilla Control Mensual</a>
            </div>
            <div className="flex gap-2">
              <label className="flex-1 cursor-pointer">
                <input type="file" accept=".xlsx,.xls" className="hidden" onChange={(e) => handleExcelUpload(e, 'expenses')} disabled={uploading} data-testid="input-upload-expenses" />
                <div className="w-full text-center py-2 px-4 rounded-md text-sm font-medium bg-[#D98A6C] hover:bg-[#C47A5C] text-white transition-colors">
                  {uploading && uploadType === 'expenses' ? 'Procesando...' : 'Subir Gastos (Flujo)'}
                </div>
              </label>
              <label className="flex-1 cursor-pointer">
                <input type="file" accept=".xlsx,.xls" className="hidden" onChange={(e) => handleExcelUpload(e, 'operational_cases')} disabled={uploading} data-testid="input-upload-cases" />
                <div className="w-full text-center py-2 px-4 rounded-md text-sm font-medium bg-[#2B4257] hover:bg-[#1A2835] text-white transition-colors">
                  {uploading && uploadType === 'operational_cases' ? 'Procesando...' : 'Subir Control Mensual'}
                </div>
              </label>
            </div>
            {uploading && (
              <div className="flex items-center gap-2 text-sm text-[#5C7083]">
                <Loader2 className="h-4 w-4 animate-spin" /> Analizando archivo...
              </div>
            )}
            {uploadResult && (
              <div className="p-3 bg-[#EDF5F0] rounded-lg space-y-1">
                <div className="flex items-center gap-2">
                  <CheckCircle className="text-[#4A7C59]" size={16} />
                  <span className="text-sm font-medium text-[#4A7C59]">Importacion completada</span>
                </div>
                <p className="text-xs text-[#5C7083]">Nuevos: {uploadResult.inserted} | Actualizados: {uploadResult.updated} | Omitidos: {uploadResult.skipped}</p>
              </div>
            )}
          </div>
        </div>

        {/* Shopify Config & Sync */}
        <div className="mt-6 p-5 border border-[#E8E2D7] rounded-xl space-y-4" data-testid="shopify-sync-card">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-[#EDF5F0] rounded-lg">
              <ShoppingBag className="text-[#4A7C59]" size={18} />
            </div>
            <div className="flex-1">
              <h4 className="font-semibold text-[#1A2835]">Shopify - Pedidos</h4>
              <p className="text-xs text-[#5C7083]">Sincroniza pedidos de tu tienda Shopify</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Input
              value={shopifyStoreUrl}
              onChange={(e) => setShopifyStoreUrlState(e.target.value)}
              placeholder="tu-tienda.myshopify.com"
              className="flex-1"
              data-testid="input-shopify-store-url"
            />
            <Button onClick={handleSaveShopifyStore} disabled={savingStoreUrl} className="btn-secondary" data-testid="btn-save-shopify-url">
              {savingStoreUrl ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save size={16} />}
            </Button>
          </div>
          <Button
            onClick={handleSyncShopify}
            disabled={syncingShopify || !shopifyStoreUrl}
            className="btn-primary gap-2 w-full"
            data-testid="btn-sync-shopify"
          >
            {syncingShopify ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw size={16} />}
            {syncingShopify ? 'Sincronizando...' : 'Sincronizar Pedidos Shopify'}
          </Button>
          {shopifySyncResult && (
            <div className="p-3 bg-[#EDF5F0] rounded-lg">
              {shopifySyncResult.error ? (
                <p className="text-sm text-[#C25953]">{shopifySyncResult.error}{shopifySyncResult.hint && <span className="block text-xs mt-1">{shopifySyncResult.hint}</span>}</p>
              ) : (
                <>
                  <span className="text-sm font-medium text-[#4A7C59]">Sincronizacion exitosa</span>
                  <div className="text-xs text-[#5C7083] mt-1">
                    Nuevos: {shopifySyncResult.new_orders || 0} | Actualizados: {shopifySyncResult.updated_orders || 0} | Total: {shopifySyncResult.total_fetched || 0}
                  </div>
                </>
              )}
            </div>
          )}
          <div className="p-2 bg-[#F5F0E8] rounded-lg">
            <p className="text-xs text-[#5C7083]">Webhook leads: <span className="font-mono">{process.env.REACT_APP_BACKEND_URL}/api/webhooks/shopify/form</span></p>
          </div>
        </div>
      </div>

      {/* Costos Fijos */}
      <div className="card" data-testid="costos-fijos-config">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-[#FBEAE9] rounded-lg">
            <DollarSign className="text-[#C25953]" size={20} />
          </div>
          <div>
            <h3 className="font-semibold text-[#1A2835]">Costos Fijos Mensuales</h3>
            <p className="text-sm text-[#5C7083]">Define el monto de costos fijos para comparar con el flujo</p>
          </div>
        </div>
        <div className="flex gap-3 items-end">
          <div className="flex-1">
            <label className="block text-xs font-medium text-[#5C7083] mb-1">Monto Mensual ($)</label>
            <input type="number" value={cfMonto} onChange={e => setCfMonto(e.target.value)}
              className="form-input w-full" placeholder="0" data-testid="input-costos-fijos-monto" />
          </div>
          <div className="flex-1">
            <label className="block text-xs font-medium text-[#5C7083] mb-1">Detalle</label>
            <input type="text" value={cfDetalle} onChange={e => setCfDetalle(e.target.value)}
              className="form-input w-full" placeholder="Ej: Arriendo + Sueldos + Servicios" data-testid="input-costos-fijos-detalle" />
          </div>
          <Button onClick={handleSaveCostosFijos} disabled={savingCF} className="btn-primary" data-testid="btn-save-costos-fijos">
            {savingCF ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save size={16} />}
          </Button>
        </div>
      </div>

      {/* Plan Types Management */}
      <div className="card" data-testid="plan-types-config">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-[#FAF0EC] rounded-lg">
            <Settings className="text-[#D98A6C]" size={20} />
          </div>
          <div>
            <h3 className="font-semibold text-[#1A2835]">Tipos de Plan</h3>
            <p className="text-sm text-[#5C7083]">Gestiona los planes disponibles en el sistema</p>
          </div>
        </div>
        <div className="flex gap-2 mb-4">
          <Input value={newPlanName} onChange={(e) => setNewPlanName(e.target.value)}
            placeholder="Nombre del nuevo plan (ej: Premium x24)"
            className="flex-1" data-testid="input-new-plan"
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAddPlan(); } }} />
          <Button className="btn-primary" onClick={handleAddPlan} disabled={!newPlanName.trim()} data-testid="btn-add-plan">
            <Plus size={16} className="mr-1" /> Agregar
          </Button>
        </div>
        <div className="space-y-1">
          <p className="text-xs text-[#5C7083] mb-2">Planes base del sistema:</p>
          <div className="flex flex-wrap gap-2 mb-3">
            {['Essential x4', 'Balance x8', 'Glow x12', 'Level Up x16', 'My Grit Era x20', 'SEMESTRAL ESSENTIAL', 'SEMESTRAL BALANCE', 'SEMESTRAL GLOW', 'ANUAL BALANCE', 'ANUAL GLOW', 'GIFT CARD', 'Clase de Prueba'].map(p => (
              <span key={p} className="text-xs px-2 py-1 bg-[#F5F0E8] text-[#5C7083] rounded-md">{p}</span>
            ))}
          </div>
          {customPlans.length > 0 && (
            <>
              <p className="text-xs text-[#5C7083] mb-2">Planes personalizados:</p>
              <div className="flex flex-wrap gap-2">
                {customPlans.map(p => (
                  <span key={p} className="text-xs px-2 py-1 bg-[#EDF5F0] text-[#4A7C59] rounded-md flex items-center gap-1">
                    {p}
                    <button onClick={() => handleDeletePlan(p)} className="text-[#C25953] hover:text-[#A63D3D] ml-1" data-testid={`btn-delete-plan-${p}`}>
                      <X size={12} />
                    </button>
                  </span>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* System Info */}
      <div className="card">
        <h3 className="font-semibold text-[#1A2835] mb-4">Información del Sistema</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="p-3 bg-[#F5F0E8] rounded-lg">
            <p className="text-xs text-[#5C7083]">Versión</p>
            <p className="font-medium">1.0.0 MVP</p>
          </div>
          <div className="p-3 bg-[#F5F0E8] rounded-lg">
            <p className="text-xs text-[#5C7083]">Virtual POS</p>
            <p className="font-medium text-[#4A7C59]">Configurado</p>
          </div>
          <div className="p-3 bg-[#F5F0E8] rounded-lg">
            <p className="text-xs text-[#5C7083]">Shopify</p>
            <p className="font-medium text-[#4A7C59]">Configurado</p>
          </div>
          <div className="p-3 bg-[#F5F0E8] rounded-lg">
            <p className="text-xs text-[#5C7083]">Base de Datos</p>
            <p className="font-medium text-[#4A7C59]">Conectado</p>
          </div>
        </div>
      </div>

      {/* Daily Note Dialog */}
      <Dialog open={showNoteDialog} onOpenChange={setShowNoteDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Nueva Instrucción Diaria</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateNote} className="space-y-4" data-testid="note-form">
            <div>
              <Label className="form-label">Contenido *</Label>
              <textarea
                value={noteContent}
                onChange={(e) => setNoteContent(e.target.value)}
                className="form-input min-h-[120px] resize-none"
                placeholder="Escribe las instrucciones o recordatorios para el equipo de recepción..."
                required
                data-testid="input-note-content"
              />
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="convert-task"
                checked={convertToTask}
                onChange={(e) => setConvertToTask(e.target.checked)}
                className="w-4 h-4 rounded border-[#E8E2D7] text-[#2B4257] focus:ring-[#2B4257]"
                data-testid="checkbox-convert-task"
              />
              <Label htmlFor="convert-task" className="text-sm cursor-pointer">
                Convertir también en tarea
              </Label>
            </div>

            <div className="flex justify-end gap-3">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setShowNoteDialog(false)}
                className="btn-secondary"
              >
                Cancelar
              </Button>
              <Button 
                type="submit" 
                className="btn-primary" 
                disabled={saving || !noteContent}
                data-testid="btn-save-note"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Publicar
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Excel Upload Preview Dialog */}
      <Dialog open={!!uploadPreview} onOpenChange={() => setUploadPreview(null)}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Vista Previa - {uploadPreview?.upload_type === 'expenses' ? 'Gastos (Flujo)' : 'Control Mensual'}
            </DialogTitle>
          </DialogHeader>
          {uploadPreview && (
            <div className="space-y-4">
              {/* Summary */}
              <div className="grid grid-cols-3 gap-3">
                <div className="p-3 bg-[#F5F0E8] rounded-lg text-center">
                  <p className="text-2xl font-bold text-[#1A2835]">{uploadPreview.total_rows}</p>
                  <p className="text-xs text-[#5C7083]">Total filas</p>
                </div>
                <div className="p-3 bg-[#EDF5F0] rounded-lg text-center">
                  <p className="text-2xl font-bold text-[#4A7C59]">{uploadPreview.new_records}</p>
                  <p className="text-xs text-[#5C7083]">Nuevos</p>
                </div>
                <div className="p-3 bg-[#FFF3E0] rounded-lg text-center">
                  <p className="text-2xl font-bold text-[#D98A6C]">{uploadPreview.duplicates}</p>
                  <p className="text-xs text-[#5C7083]">Duplicados</p>
                </div>
              </div>

              {/* Duplicates detail */}
              {uploadPreview.duplicates > 0 && (
                <div className="border border-[#D98A6C] rounded-lg p-4 space-y-3">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="text-[#D98A6C]" size={18} />
                    <h4 className="font-semibold text-[#1A2835]">Registros Duplicados Detectados</h4>
                  </div>
                  <div className="max-h-48 overflow-y-auto space-y-2">
                    {uploadPreview.preview.filter(r => r.is_duplicate).slice(0, 20).map((item, idx) => (
                      <div key={idx} className="p-2 bg-[#FFF8F0] rounded text-xs border border-[#F0E0D0]">
                        <p className="font-medium text-[#1A2835]">
                          Fila {item.row}: {uploadPreview.upload_type === 'expenses' 
                            ? `${item.data.description} - $${(item.data.amount || 0).toLocaleString()} (${item.data.payment_date})`
                            : `${item.data.client_name} - ${item.data.tipo_accion} (${item.data.fecha_accion})`
                          }
                        </p>
                        <p className="text-[#5C7083] mt-1">
                          Ya existe: {uploadPreview.upload_type === 'expenses'
                            ? `${item.existing?.description} - $${(item.existing?.amount || 0).toLocaleString()} (${item.existing?.payment_date})`
                            : `${item.existing?.client_name} - ${item.existing?.tipo_accion} (${item.existing?.fecha_accion})`
                          }
                        </p>
                      </div>
                    ))}
                    {uploadPreview.preview.filter(r => r.is_duplicate).length > 20 && (
                      <p className="text-xs text-[#5C7083] text-center">... y {uploadPreview.duplicates - 20} duplicados mas</p>
                    )}
                  </div>
                </div>
              )}

              {/* New records preview */}
              {uploadPreview.new_records > 0 && (
                <div className="border border-[#4A7C59] rounded-lg p-4 space-y-2">
                  <h4 className="font-semibold text-[#4A7C59] text-sm">Nuevos registros ({uploadPreview.new_records})</h4>
                  <div className="max-h-48 overflow-y-auto space-y-1">
                    {uploadPreview.preview.filter(r => !r.is_duplicate).slice(0, 20).map((item, idx) => (
                      <div key={idx} className="text-xs text-[#1A2835] p-1 bg-[#F5FFF8] rounded">
                        {uploadPreview.upload_type === 'expenses' 
                          ? `${item.data.description} - $${(item.data.amount || 0).toLocaleString()} (${item.data.payment_date})`
                          : `${item.data.client_name} - ${item.data.tipo_accion} (${item.data.fecha_accion})`
                        }
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Action buttons */}
              <div className="flex flex-col gap-2 pt-2 border-t border-[#E8E2D7]">
                <Button
                  className="w-full bg-[#4A7C59] hover:bg-[#3D6B4A] text-white gap-2"
                  onClick={() => handleConfirmUpload('skip')}
                  disabled={confirming}
                  data-testid="btn-import-skip-dupes"
                >
                  {confirming ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle size={16} />}
                  Importar solo nuevos ({uploadPreview.new_records}) — Omitir duplicados
                </Button>
                {uploadPreview.duplicates > 0 && (
                  <Button
                    className="w-full bg-[#D98A6C] hover:bg-[#C47A5C] text-white gap-2"
                    onClick={() => handleConfirmUpload('update')}
                    disabled={confirming}
                    data-testid="btn-import-update-dupes"
                  >
                    {confirming ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw size={16} />}
                    Importar nuevos + Actualizar duplicados ({uploadPreview.total_rows})
                  </Button>
                )}
                <Button
                  variant="outline"
                  className="w-full btn-secondary gap-2"
                  onClick={() => setUploadPreview(null)}
                  data-testid="btn-cancel-upload"
                >
                  <X size={16} /> Cancelar
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Configuracion;
